import { formatCurrency } from "@/lib/utils";
import { TrendingDown, AlertCircle, DollarSign, Zap, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface PriceDiscrepancyCardProps {
    itemsCount: number;
    totalDiscrepancy: number;
    onViewUnderscopedItems?: () => void;
}

export function PriceDiscrepancyCard({ itemsCount, totalDiscrepancy, onViewUnderscopedItems }: PriceDiscrepancyCardProps) {
    return (
        <Card className="border border-orange-500/20 dark:border-orange-500/20 bg-gradient-to-br from-orange-50/20 to-orange-50/10 dark:from-orange-950/10 dark:to-orange-950/5">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-orange-500/80 to-amber-500/80 rounded-lg">
                        <TrendingDown className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <CardTitle className="text-base font-bold text-orange-700 dark:text-orange-300">
                            Items with a scope or price discrepancy
                        </CardTitle>
                        <Badge variant="outline" className="text-xs mt-1 border-orange-500/20 bg-orange-50/20 dark:bg-orange-900/10">
                            <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                            Contractor Bid Exceeds Insurance
                        </Badge>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-white/70 dark:bg-gray-900/50 rounded-lg p-2.5 border border-orange-200/50 dark:border-orange-800/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <DollarSign className="h-3.5 w-3.5 text-orange-600" />
                            <p className="text-xs text-muted-foreground">Total Discrepancy</p>
                        </div>
                        <p className="text-lg font-bold text-orange-700 dark:text-orange-300">{formatCurrency(totalDiscrepancy)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/50 dark:to-amber-900/50 rounded-lg p-2.5 border border-orange-300/50 dark:border-orange-700/50">
                        <div className="flex items-center gap-1 mb-0.5">
                            <AlertCircle className="h-3.5 w-3.5 text-orange-600" />
                            <p className="text-xs text-muted-foreground">Requires Review</p>
                        </div>
                        <p className="text-lg font-bold text-orange-700 dark:text-orange-300">{itemsCount}</p>
                    </div>
                </div>

                {/* Action buttons */}
                <Button
                    size="sm"
                    className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white"
                    onClick={onViewUnderscopedItems}
                    data-testid="button-view-underscoped"
                >
                    <Zap className="h-3.5 w-3.5 mr-1.5" />
                    Review Items
                </Button>
            </CardContent>
        </Card>
    );
}
